import React, { useState } from "react";
import { Modal, Button, message } from "antd";
import { FormattedMessage, useIntl } from "react-intl";
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { getStripe } from "../../utils/stripe";
import { isIntentSucceeded } from "../../utils/stripeCharge";

/**
 * Reusable Stripe Payment Element modal (contract charge + add-card).
 *
 * The parent passes a `clientSecret` (a PaymentIntent secret for a charge, a
 * SetupIntent secret for add-card) and the `mode`. We mount <Elements> for that
 * one secret, collect the card in Stripe's iframe (PCI SAQ A — no card data in
 * our DOM), and confirm with `redirect: 'if_required'` so only SCA/3DS cards
 * leave the page. `onSuccess` fires ONLY when the intent reaches `succeeded`
 * (payment) / `succeeded` (setup) — never on cancel/failure, so the caller never
 * advances contract status without a real, confirmed charge.
 */
const PaymentElementForm = ({
  mode,
  clientSecret,
  submitLabel,
  onSuccess,
  onCancel,
}) => {
  const intl = useIntl();
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);

  const handleConfirm = async () => {
    if (!stripe || !elements || submitting) {
      return;
    }
    setSubmitting(true);
    const { error: submitError } = await elements.submit();
    if (submitError) {
      setSubmitting(false);
      message.error(
        submitError.message || intl.formatMessage({ id: "PAYMENT_FAILED" })
      );
      return;
    }

    const confirmParams = { return_url: window.location.href };
    const result =
      mode === "setup"
        ? await stripe.confirmSetup({
            elements,
            clientSecret,
            confirmParams,
            redirect: "if_required",
          })
        : await stripe.confirmPayment({
            elements,
            clientSecret,
            confirmParams,
            redirect: "if_required",
          });

    setSubmitting(false);

    if (result.error) {
      message.error(
        result.error.message || intl.formatMessage({ id: "PAYMENT_FAILED" })
      );
      return;
    }
    // SEC-8: advance only on a genuinely successful intent.
    if (isIntentSucceeded(result)) {
      onSuccess(result.paymentIntent || result.setupIntent);
      return;
    }
    // Non-error, non-succeeded (e.g. `processing`): do NOT advance status. Close
    // the flow with a note — the webhook is the source of truth and will advance
    // the contract when the payment clears.
    message.info(intl.formatMessage({ id: "PAYMENT_PROCESSING" }));
    onCancel();
  };

  return (
    <div className="stripe-payment-element">
      {/* Cards-only for launch: restrict the Element to card and suppress the
          Link / wallet (Apple/Google Pay) surfaces client-side. The authoritative
          restriction is the backend PaymentIntent `payment_method_types:["card"]`;
          this is defense-in-depth so Link/BNPL never render even if the intent
          still lists them. */}
      <PaymentElement
        options={{
          paymentMethodOrder: ["card"],
          wallets: { applePay: "never", googlePay: "never" },
        }}
      />
      <div className="d-flex justify-content-end mt-4">
        <Button onClick={onCancel} disabled={submitting} className="me-2">
          <FormattedMessage id="CANCEL" />
        </Button>
        <Button
          type="primary"
          onClick={handleConfirm}
          loading={submitting}
          disabled={!stripe}
        >
          {submitLabel}
        </Button>
      </div>
    </div>
  );
};

const PaymentElementModal = ({
  visible,
  mode = "payment",
  clientSecret,
  title,
  submitLabel,
  onSuccess,
  onCancel,
}) => {
  const stripePromise = getStripe();

  return (
    <Modal
      open={visible}
      title={title}
      footer={null}
      onCancel={onCancel}
      destroyOnClose
    >
      {clientSecret && stripePromise ? (
        <Elements stripe={stripePromise} options={{ clientSecret }}>
          <PaymentElementForm
            mode={mode}
            clientSecret={clientSecret}
            submitLabel={submitLabel || <FormattedMessage id="PAY_NOW" />}
            onSuccess={onSuccess}
            onCancel={onCancel}
          />
        </Elements>
      ) : (
        <div className="text-center p-4">
          <FormattedMessage id="PREPARING_PAYMENT" />
        </div>
      )}
    </Modal>
  );
};

export default PaymentElementModal;
